import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { keepPreviousData } from '@tanstack/react-query';
import { AlertTriangle, Bot, Check, ChevronDown, ChevronLeft, Loader2, Plus, Search, SquareCheckBig, SquarePen, X } from 'lucide-react';
import { Button } from '../../components/ui/button';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';
import { Popover, PopoverContent, PopoverTrigger } from '../../components/ui/popover';
import BotAvatar from '../../components/bots/BotAvatar';
import BotName from '../../components/bots/BotName';
import ProjectList from '../../components/bots/ProjectList';
import { useBotLabel } from '../../components/bots/botDisplay';
import { useDefaultChatBot } from '../../components/bots/defaultBot';
import { chatFilters, chatPath, chatsPath, filterQuery, newChatPath } from '../../components/bots/chatLinks';
import { ProjectDot } from '../../components/layout/SidebarParts';
import ChatUnreadDot from '../../components/unread/ChatUnreadDot';
import { useAllChatsQuery, useBotChatQuery, useMyBotsQuery, useProjectsQuery } from '../../hooks/queries';
import { useMediaQuery, SCREEN } from '../../hooks/useMediaQuery';
import { bucketOf, when } from '../../lib/recentWork';
import { cn } from '../../lib/utils';
import BotPage from './BotPage';

// Next to the history the chat has less room: the computer and the project's details open
// beside it only on wide screens (narrower, above it or in a sheet, as on tablets).
const WIDE_CHAT = '(min-width: 1440px)';

const chip = (on) => cn(
  'inline-flex items-center gap-1.5 h-7 max-md:h-8 px-2 max-md:px-2.5 max-w-[200px] rounded-full border text-[12px] max-md:text-[13px] whitespace-nowrap flex-shrink-0 transition-colors',
  on ? 'bg-accent border-transparent text-foreground font-medium' : 'border-border text-muted-foreground hover:text-foreground',
);

/** The next page, asked for when the end of the list comes into view (or with the button). */
const LoadMore = ({ onMore, busy, label }) => {
  const ref = useRef(null);
  const more = useRef(onMore);
  more.current = onMore;
  useEffect(() => {
    const el = ref.current;
    if (!el || busy) return undefined;
    const seen = new IntersectionObserver(([e]) => { if (e.isIntersecting) more.current(); }, { rootMargin: '240px 0px' });
    seen.observe(el);
    return () => seen.disconnect();
  }, [busy]);
  return (
    <div ref={ref} className="flex justify-center py-3">
      <Button variant="ghost" size="sm" onClick={onMore} disabled={busy} className="gap-1.5 text-muted-foreground" data-testid="chats-more">
        {busy && <Loader2 className="w-3.5 h-3.5 animate-spin" />}{label}
      </Button>
    </div>
  );
};

/** One chat in the history: its bee, title and time, "Bot · Project", and what waits in it. */
const ChatRow = ({ chat: c, bot, active, onOpen, wide, hideProject }) => {
  const { t, i18n } = useTranslation();
  const waits = c.pending_approvals > 0;
  return (
    <button type="button" onClick={() => onOpen(c)} aria-current={active ? 'true' : undefined} data-testid="chats-row"
      className={cn('w-full text-left flex items-center md:items-start gap-3 px-4 py-2.5 transition-colors',
        active ? 'bg-accent' : 'hover:bg-accent/60 active:bg-accent/60')}>
      <BotAvatar bot={bot} size={wide ? 30 : 40} state={c.working ? 'working' : null} />
      <span className="flex-1 min-w-0">
        <span className="flex items-center gap-2 min-w-0">
          {c.task_id && <SquareCheckBig className="w-3.5 h-3.5 shrink-0 text-muted-foreground" />}
          <span className={cn('flex-1 min-w-0 truncate text-[15px] md:text-sm text-foreground', c.unread ? 'font-semibold' : 'font-medium')}>
            {c.title || t('bots.chat.new_chat')}
          </span>
          {waits ? (
            <span className="w-2 h-2 rounded-full bg-attention flex-shrink-0" title={t('bots.state.needs_you')}
              aria-label={t('bots.state.needs_you')} data-testid="chats-row-needs-you" />
          ) : (
            <span className="text-xs text-muted-foreground tabular-nums flex-shrink-0">{when(c.updated_at || c.created_at, i18n.language)}</span>
          )}
        </span>
        <span className="mt-0.5 flex items-center gap-1.5 min-w-0 text-[13px] md:text-[12.5px] text-muted-foreground">
          <BotName bot={bot} className={cn('min-w-0 shrink-0', hideProject ? 'max-w-full' : 'max-w-[60%]')} />
          {!hideProject && <span aria-hidden="true">·</span>}
          {!hideProject && <span className="flex-1 min-w-0 truncate">{c.project?.name || t('common.no_project')}</span>}
          {hideProject && <span className="flex-1" />}
          {c.unread && !waits && <ChatUnreadDot />}
        </span>
        {c.snippet && <span className="mt-0.5 block text-xs text-muted-foreground line-clamp-2">{c.snippet}</span>}
      </span>
    </button>
  );
};

/**
 * Every chat, with every bot, in every project and in none (backend GET /bot-chats): a search,
 * filters (Needs you, a bot, a project) and the chats by last activity, a page at a time.
 * `project`: a project's Chats tab, the same history of that project only (no project filter,
 * no page title).
 */
export const ChatsHistory = ({ filters: given, onFilters, activeId, onOpen, onNew, onBots, scroll, project }) => {
  const filters = project ? { ...given, projectId: project.id } : given;
  const { t } = useTranslation();
  const botLabel = useBotLabel();
  const [q, setQ] = useState('');
  const [search, setSearch] = useState('');
  useEffect(() => {
    const id = setTimeout(() => setSearch(q.trim().length >= 2 ? q.trim() : ''), 250);
    return () => clearTimeout(id);
  }, [q]);
  const [projectsOpen, setProjectsOpen] = useState(false);
  const { data: mine } = useMyBotsQuery();
  const { data: allProjects } = useProjectsQuery();
  const projects = useMemo(() => (allProjects || []).filter((p) => !p.deleted_at), [allProjects]);
  // BusyBee first, then the bots, as the composer lists them.
  const bots = useMemo(() => [...(mine?.queen ? [mine.queen] : []), ...(mine?.bots || [])], [mine]);
  const byId = useMemo(() => new Map(bots.map((b) => [b.id, b])), [bots]);
  // A link names BusyBee 'queen' (she has no id before she is made).
  const botFilter = filters.botId === 'queen' ? mine?.queen?.id || 'queen' : filters.botId;
  const shownBot = botFilter ? byId.get(botFilter) : null;
  const shownProject = filters.projectId && filters.projectId !== 'none' ? projects.find((p) => p.id === filters.projectId) : null;

  const query = useAllChatsQuery({
    ...(search ? { q: search } : {}),
    ...(botFilter ? { bot_id: botFilter } : {}),
    ...(filters.projectId ? { project_id: filters.projectId } : {}),
    ...(filters.needsYou ? { needs_you: true } : {}),
  }, { placeholderData: keepPreviousData });
  const pages = query.data?.pages;
  const chats = useMemo(() => (pages || []).flatMap((p) => p.chats || []), [pages]);
  const waiting = pages?.[0]?.needs_you || 0;
  const filtered = !!(search || botFilter || filters.projectId || filters.needsYou);

  const groups = useMemo(() => {
    const out = { today: [], yesterday: [], earlier: [] };
    chats.forEach((c) => {
      const at = bucketOf(c.updated_at || c.created_at);
      out[at === 'today' || at === 'yesterday' ? at : 'earlier'].push(c);
    });
    return [['today', 'bots.chat.group_today'], ['yesterday', 'bots.chat.group_yesterday'], ['earlier', 'bots.chats.earlier']]
      .map(([key, label]) => [key, label, out[key]]).filter(([, , rows]) => rows.length);
  }, [chats]);

  const set = (change) => onFilters({ ...filters, ...change });
  const iconButton = 'w-9 h-9 shrink-0 inline-flex items-center justify-center rounded-full text-muted-foreground hover:text-foreground hover:bg-accent';

  const list = query.isLoading ? (
    <div className="py-10 flex justify-center"><Loader2 className="w-5 h-5 animate-spin text-muted-foreground" /></div>
  ) : chats.length === 0 ? (
    filtered ? (
      <p className="px-4 py-10 text-center text-sm text-muted-foreground" data-testid="chats-empty">{t('bots.chat.search_empty')}</p>
    ) : (
      <div className="px-6 py-12 text-center" data-testid="chats-empty">
        <p className="text-sm font-medium text-foreground">{t('bots.chats.empty')}</p>
        <p className="mt-1 text-sm text-muted-foreground">{t('bots.chats.empty_hint')}</p>
      </div>
    )
  ) : (
    <div className={cn('pb-2 transition-opacity', query.isPlaceholderData && 'opacity-60')}>
      {groups.map(([key, label, rows]) => (
        <section key={key} aria-label={t(label)}>
          <h2 className="px-4 pt-3 pb-1 text-[11.5px] font-semibold text-muted-foreground">{t(label)}</h2>
          {rows.map((c) => (
            <ChatRow key={c.id} chat={c} bot={byId.get(c.bot_id) || { id: c.bot_id }} active={c.id === activeId} onOpen={onOpen}
              wide={scroll || !!project} hideProject={!!project} />
          ))}
        </section>
      ))}
      {query.hasNextPage && (
        <LoadMore onMore={() => query.fetchNextPage()} busy={query.isFetchingNextPage} label={t('notifications.load_more')} />
      )}
    </div>
  );

  return (
    <div className={cn('flex flex-col', scroll && 'h-full min-h-0')} data-testid="chats-history">
      <div className="flex-shrink-0">
        {!project && <div className="hidden md:flex items-center gap-1.5 px-4 pt-4 pb-3">
          <h1 className="flex-1 min-w-0 truncate text-lg font-semibold text-foreground">{t('nav.chats')}</h1>
          <Button variant="ghost" size="sm" onClick={onBots} className="gap-1.5 text-muted-foreground" data-testid="chats-bots">
            <Bot className="w-4 h-4" />{t('bots.hub.title')}
          </Button>
          <Button variant="outline" size="sm" onClick={onNew} className="gap-1.5" data-testid="chats-new">
            <Plus className="w-4 h-4" />{t('bots.chat.new_chat')}
          </Button>
        </div>}
        <div className={cn('flex items-center gap-1 pb-2', project ? 'px-0 pt-0' : 'px-4 max-md:pt-2')}>
          <label className="flex-1 min-w-0 flex items-center gap-2 h-9 md:h-8 px-2.5 rounded-lg border border-border focus-within:border-amber-500/60">
            {query.isFetching && search ? <Loader2 className="w-3.5 h-3.5 animate-spin text-muted-foreground shrink-0" />
              : <Search className="w-3.5 h-3.5 text-muted-foreground shrink-0" />}
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder={t('bots.chat.search_placeholder')}
              aria-label={t('bots.chat.search_placeholder')} data-testid="chats-search"
              className="flex-1 min-w-0 bg-transparent text-[15px] md:text-[13px] text-foreground outline-none placeholder:text-muted-foreground/70" />
            {q && (
              <button type="button" onClick={() => setQ('')} aria-label={t('common.close')} className="text-muted-foreground hover:text-foreground">
                <X className="w-3.5 h-3.5" />
              </button>
            )}
          </label>
          {!project && <button type="button" onClick={onBots} className={cn(iconButton, 'md:hidden')} aria-label={t('bots.hub.title')} title={t('bots.hub.title')}>
            <Bot className="w-5 h-5" />
          </button>}
          {!project && <button type="button" onClick={onNew} className={cn(iconButton, 'md:hidden -mr-2')} aria-label={t('bots.chat.new_chat')}
            title={t('bots.chat.new_chat')} data-testid="chats-new-mobile">
            <SquarePen className="w-5 h-5" />
          </button>}
        </div>
        {/* One row everywhere (the user, 2026-10-31): what does not fit scrolls sideways. */}
        <div className={cn('flex gap-1.5 pb-2 overflow-x-auto bb-no-scrollbar', project ? 'px-0' : 'px-4')} role="group" data-testid="chats-filters">
          <button type="button" className={chip(!botFilter && (project || !filters.projectId) && !filters.needsYou)} data-testid="chats-filter-all"
            onClick={() => onFilters({})}>
            {t('common.all')}
          </button>
          <button type="button" className={chip(filters.needsYou)} aria-pressed={filters.needsYou} data-testid="chats-filter-needs-you"
            onClick={() => set({ needsYou: !filters.needsYou })}>
            {t('bots.chat.group_needs_you')}
            {waiting > 0 && <span className="tabular-nums text-orange-600 dark:text-attention-text">{waiting}</span>}
          </button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button type="button" className={chip(!!botFilter)} data-testid="chats-filter-bot">
                {shownBot && <BotAvatar bot={shownBot} size="xs" />}
                <span className="truncate">{shownBot ? botLabel(shownBot) : t('bots.chats.any_bot')}</span>
                <ChevronDown className="w-3 h-3 shrink-0" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="w-64 max-h-80 overflow-y-auto">
              <DropdownMenuItem onSelect={() => set({ botId: null })} className="gap-2.5">
                <span className="flex-1">{t('bots.chats.any_bot')}</span>
                {!botFilter && <Check className="w-4 h-4 text-foreground shrink-0" />}
              </DropdownMenuItem>
              {bots.length > 0 && <DropdownMenuSeparator />}
              {bots.map((b) => (
                <DropdownMenuItem key={b.id} onSelect={() => set({ botId: b.id })} className="gap-2.5" data-testid="chats-filter-bot-option">
                  <BotAvatar bot={b} size="sm" />
                  <BotName bot={b} className="flex-1 min-w-0 text-sm" />
                  {b.id === botFilter && <Check className="w-4 h-4 text-foreground shrink-0" />}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
          {!project && <Popover open={projectsOpen} onOpenChange={setProjectsOpen}>
            <PopoverTrigger asChild>
              <button type="button" className={chip(!!filters.projectId)} data-testid="chats-filter-project">
                {shownProject && <ProjectDot project={shownProject} />}
                <span className="truncate">
                  {filters.projectId === 'none' ? t('common.no_project') : shownProject?.name || t('bots.chats.any_project')}
                </span>
                <ChevronDown className="w-3 h-3 shrink-0" />
              </button>
            </PopoverTrigger>
            <PopoverContent align="start" className="w-72 p-0">
              <ProjectList compact autoFocus projects={projects} projectId={filters.projectId}
                anyLabel={t('bots.chats.any_project')} noneLabel={t('common.no_project')}
                onPick={(id) => { setProjectsOpen(false); set({ projectId: id }); }} />
            </PopoverContent>
          </Popover>}
        </div>
      </div>
      {scroll ? <div className="flex-1 min-h-0 overflow-y-auto" data-testid="chats-list">{list}</div>
        : <div className={cn(project && 'rounded-xl border border-border bg-card overflow-hidden')} data-testid="chats-list">{list}</div>}
    </div>
  );
};

/** What the chat pane shows when there is nothing to show: a loader, or why (with the way back on phones). */
const PaneNote = ({ text, onBack }) => {
  const { t } = useTranslation();
  return (
    <div className="h-full flex flex-col">
      {onBack && (
        <div className="flex items-center h-[calc(3.5rem+env(safe-area-inset-top))] px-1 flex-shrink-0" style={{ paddingTop: 'env(safe-area-inset-top)' }}>
          <button type="button" onClick={onBack} aria-label={t('common.back')} title={t('common.back')}
            className="w-9 h-9 inline-flex items-center justify-center rounded-full text-foreground hover:bg-accent">
            <ChevronLeft className="w-6 h-6" />
          </button>
        </div>
      )}
      <div className="flex-1 flex items-center justify-center gap-2 px-6 text-center text-sm text-muted-foreground">
        {text ? <><AlertTriangle className="w-4 h-4 text-amber-500 shrink-0" />{text}</>
          : <Loader2 className="w-5 h-5 animate-spin" />}
      </div>
    </div>
  );
};

/** A chat that exists: its bot and project come with the link from the history, else from the chat. */
const OpenChat = ({ chatId, host }) => {
  const { t } = useTranslation();
  const { state } = useLocation();
  const { data, isError } = useBotChatQuery(chatId, { retry: false });
  const chat = data?.chat?.id === chatId ? data.chat : null;
  const botId = chat?.bot_id || (state?.chatId === chatId ? state.botId : null);
  const projectId = chat ? chat.project_id || null : state?.chatId === chatId ? state.projectId || null : null;
  const params = useMemo(() => ({ chat: chatId, ...(projectId ? { project: projectId } : {}) }), [chatId, projectId]);
  if (!chat && isError) return <PaneNote text={t('bots.chats.not_found')} onBack={host.onBack} />;
  if (!botId) return <PaneNote onBack={host.onBack} />;
  return <BotPage key={botId} botId={botId} embedParams={params} {...host.props(botId, projectId)} />;
};

/**
 * A new chat: with the bot the link names, else the default one (components/bots/defaultBot.js:
 * BusyBee outside a project, in one the bot used there last). Drawn once that bot is known, so
 * it never opens with one bot and turns into another.
 */
const NewChat = ({ botParam, projectParam, host }) => {
  const { data: projects, isSuccess: projectsLoaded } = useProjectsQuery();
  const named = projectParam && projectParam !== 'none' ? projectParam : null;
  const project = named ? (projects || []).find((p) => p.id === named) || null : null;
  const explicit = botParam && botParam !== 'queen' ? botParam : null;
  const projectKnown = !named || projectsLoaded;
  const fallback = useDefaultChatBot(project, { enabled: !explicit && projectKnown });
  const botId = explicit || (projectKnown && fallback.ready ? fallback.bot?.id : null);
  const projectId = project?.id || (explicit ? named : null);
  const params = useMemo(() => ({ chat: 'new', ...(projectId ? { project: projectId } : {}) }), [projectId]);
  if (!botId) return <PaneNote onBack={host.onBack} />;
  return <BotPage key={botId} botId={botId} embedParams={params} {...host.props(botId, projectId)} />;
};

/**
 * Chats: the one history of every chat (all bots, all projects) and a chat in it.
 * /app/chats is the history (filters in the link: chatFilters), /app/chats/<id> a chat,
 * /app/chats/new a new one (?bot=, ?project=). On computers the history and the chat side by
 * side (with no chat open, a new one with BusyBee); on phones the history, and a chat full
 * screen with the arrow back to it. The chat itself is BotPage, told which chat it is.
 */
const ChatsPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { chatId: chatParam } = useParams();
  const wide = useMediaQuery(SCREEN.md);
  const isNew = chatParam === 'new';
  const chatId = chatParam && !isNew ? chatParam : null;
  // The history's filters ride along in the links of its chats; ?bot= and ?project= of a new
  // chat are who and where it starts instead.
  const filters = useMemo(() => (isNew ? {} : chatFilters(location.search)), [isNew, location.search]);
  const keep = filterQuery(filters);
  const withFilters = (path) => (keep ? `${path}?${keep}` : path);
  const listPath = chatsPath(filters);
  const fromList = !!location.state?.fromList;

  const onFilters = (next) => {
    const query = filterQuery(next);
    if (!chatParam || isNew) navigate(chatsPath(next), { replace: true });
    else navigate({ pathname: location.pathname, search: query ? `?${query}` : '' }, { replace: true, state: location.state });
  };
  const openChat = (c) => navigate(withFilters(chatPath(c.id)), {
    state: { chatId: c.id, botId: c.bot_id, projectId: c.project_id || null, fromList: true },
  });
  const startNew = () => { if (!isNew) navigate(newChatPath(), { state: { fromList: true } }); };
  const back = () => (fromList ? navigate(-1) : navigate(listPath, { replace: true }));

  // What the chat asks of its host (BotPage's embed props): a chat it made opens here, another
  // bot or project starts a new chat, a deleted one goes back to the history.
  const host = {
    onBack: wide ? undefined : back,
    props: (botId, projectId) => ({
      wideAt: WIDE_CHAT,
      onBack: wide ? undefined : back,
      onDeleted: () => navigate(listPath, { replace: true }),
      onEmbedParams: (next) => {
        const state = { fromList };
        if (next.chat && next.chat !== 'new') {
          if (next.chat === chatId) return;  // the same chat (moved into a project)
          navigate(withFilters(chatPath(next.chat)), { replace: !!chatParam, state: { ...state, chatId: next.chat, botId, projectId: next.project || null } });
        } else {
          navigate(newChatPath({ botId, projectId: next.project }), { replace: true, state });
        }
      },
      onEmbedBot: (id) => navigate(newChatPath({ botId: id, projectId }), { replace: isNew, state: { fromList } }),
    }),
  };

  const params = new URLSearchParams(location.search);
  const pane = chatId ? <OpenChat chatId={chatId} host={host} />
    : <NewChat botParam={isNew ? params.get('bot') : null} projectParam={isNew ? params.get('project') : null} host={host} />;

  const history = (
    <ChatsHistory filters={filters} onFilters={onFilters} activeId={chatId} onOpen={openChat} onNew={startNew}
      onBots={() => navigate('/app/bots')} scroll={wide} />
  );

  if (wide) {
    return (
      <div className="h-full min-h-0 flex" data-testid="chats-page">
        <section aria-label={t('nav.chats')} className="w-[340px] lg:w-[360px] flex-shrink-0 min-h-0 border-r border-border">{history}</section>
        <div className="flex-1 min-w-0 min-h-0 flex flex-col" data-testid="chats-pane">{pane}</div>
      </div>
    );
  }
  // Phones: the history, or one chat full screen (no tab bar, its own header, AppLayout).
  if (chatParam) return <div className="h-full min-h-0 flex flex-col" data-testid="chats-pane">{pane}</div>;
  return <div className="max-md:-mx-3 max-md:-mt-2" data-testid="chats-page">{history}</div>;
};

export default ChatsPage;
